import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useGetUserQuery } from 'common'
import { ANALYTICS_PAYWALL_SHOWN_PROPS_MAP } from 'components/empty_state/constants'
import {
  isSubscriptionLockedSelector,
  isDowngradeScheduledSelector,
  isSubscriptionExpiringSelector,
  isAvailableFeatureSelector,
  hasAddonsSelector,
  isFreeOrTrialSelector
} from 'features/billing'
import { useActivationQ3Flag } from 'features/empty_state'
import { TRACKING_EVENT_NAMES, useTrackOnNavigation } from 'hooks/analytics_events'
import { isAdminOrOwnerSelector } from 'selectors/current_user'

import { AdminBanner } from './admin_banner'
import { NonAdminBanner } from './non_admin_banner'

export const UpgradeBanner = ({ featureKey, contentKey }) => {
  const { data: { id: userId } = {} } = useGetUserQuery()
  const isActivationQ3FlagEnabled = useActivationQ3Flag()
  const isAdmin = useSelector(isAdminOrOwnerSelector)
  const isSubscriptionLocked = useSelector(isSubscriptionLockedSelector)
  const isDowngradeScheduled = useSelector(isDowngradeScheduledSelector)
  const isSubscriptionExpiring = useSelector(isSubscriptionExpiringSelector)
  const hasAddons = useSelector(hasAddonsSelector)
  const isFeatureAvailable = useSelector((state) => isAvailableFeatureSelector(state, { featureKey }))
  const isTabletDown = useDeviceType('isTabletDown')
  const freeOrTrial = useSelector(isFreeOrTrialSelector)

  const properties = (
    ANALYTICS_PAYWALL_SHOWN_PROPS_MAP('inline')[contentKey]
    || ANALYTICS_PAYWALL_SHOWN_PROPS_MAP('inline')[featureKey]
  )

  let noUpgradePrompt = isSubscriptionLocked
    || isDowngradeScheduled
    || isSubscriptionExpiring
    || hasAddons
    || isFeatureAvailable
    || !isActivationQ3FlagEnabled
    || !featureKey
    || isTabletDown

  if (noUpgradePrompt && featureKey === 'admin_dashboard' && (!isAdmin || freeOrTrial)) {
    noUpgradePrompt = false
  }

  // Only track when banner will actually be displayed and properties are defined
  const shouldTrack = !noUpgradePrompt && properties && userId

  // Memoize the config object to prevent recreation on every render
  const trackingConfig = useMemo(() => ({
    name: TRACKING_EVENT_NAMES.PAYWALL_SHOWN,
    version: 1,
    dependencies: { user_id: userId },
    entities: ['attribution', 'organization', 'user'],
    properties
  }), [userId, properties])

  useTrackOnNavigation(shouldTrack, trackingConfig)

  if (noUpgradePrompt) return null

  const BannerComponent = isAdmin ? AdminBanner : NonAdminBanner

  return (
    <BannerComponent featureKey={featureKey} contentKey={contentKey} />
  )
}
